<!-- app-subdomain-creators/src/Components/PlatformaVideoPlayer.vue -->
<template>
  <div
  class=" w-full h-full bg-transparent rounded-2xl overflow-hidden flex items-center justify-center group"
  @mousemove="showUi"
  @mouseleave="hideUiDelayed"
>

    <!-- VIDEO -->
    <video
      ref="videoEl"
      class=" w-full h-full object-contain bg-transparent rounded-2xl"
      playsinline
      preload="auto"
      @timeupdate="updateProgress"
      @loadedmetadata="setDuration"
      @loadeddata="onVideoLoaded"
      @click="togglePlay"
    ></video>


<!-- LOADING OVERLAY (WITH FADE) -->
<transition name="loading-fade">
  <div
    v-if="isLoading"
    class="absolute inset-0 flex items-center justify-center bg-transparent rounded-2xl"
  >
    <div class="relative size-20">
      <div class="absolute inset-0 rounded-full border-[6px] border-opacity-20 border-[#2D8CFF]"></div>
      <div class="size-20 animate-spin rounded-full border-[6px] border-[#2D8CFF] border-t-transparent"></div>
    </div>
  </div>
</transition>


    <!-- BIG CENTER PLAY BUTTON -->
    <div
      v-if="isPaused && showOverlay && !isLoading"
      @click.stop="togglePlay"
      class="absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300"
    >
      <div class="flex items-center bg-[#2D8CFF] p-5 rounded-full hover:bg-[#2d8bffb2] opacity-70 transition">
        <PlayIcon class="text-white/70 w-16 h-16 mx-auto pl-2"></PlayIcon>
      </div>
    </div>

    <!-- CONTROL BAR -->
    <transition name="fade">
      <div
        v-if="showControls && !isLoading"
        class="absolute bottom-0 left-0 right-0 px-4 pt-8 pb-1 flex flex-col gap-2"
        @mouseenter="onControlsEnter"
        @mouseleave="onControlsLeave"
      >
        <!-- TIMELINE + HOVER PREVIEW -->
        <div class="relative w-full">
          <!-- Hover preview bubble -->
          <transition name="fade">
            <div
              v-if="isHoveringBar"
              class="absolute -top-20"
              :style="{ left: hoverPercent + '%' }"
              style="transform: translateX(-50%);"
            >
              <div class="flex flex-col items-center">
                <!-- Placeholder preview box (can later become a real frame) -->
                <div
                  class="w-40 h-24 bg-black/90 border border-white/10 rounded-lg flex items-center justify-center text-xs text-white/70"
                >
                  {{ hoverFormattedTime }}
                </div>
                <div
                  class="mt-1 px-2 py-0.5 rounded-full bg-black/90 text-[10px] text-white/80"
                >
                  {{ hoverFormattedTime }}
                </div>
              </div>
            </div>
          </transition>

          <!-- Progress bar -->
          <div
            ref="progressBarEl"
            class="relative w-full h-2 bg-white/20 rounded-full cursor-pointer overflow-hidden"
            @mousemove="onBarMouseMove"
            @mouseleave="onBarMouseLeave"
            @mousedown.prevent="onBarMouseDown"
          >
            <!-- Hover indicator line -->
            <div
              v-if="isHoveringBar"
              class="absolute top-0 bottom-0 w-[2px] bg-white/40"
              :style="{ left: hoverPercent + '%' }"
            ></div>

            <!-- Played progress -->
            <div
              class="absolute top-0 left-0 h-2 rounded-full bg-[#2D8CFF] transition-all"
              :style="{ width: progress + '%' }"
            ></div>
          </div>
        </div>


        <!-- BOTTOM ROW — YouTube style pill controls -->
<div class="flex items-center justify-between text-white text-[13px] mt-2 select-none">

  <!-- LEFT SIDE -->
  <div class="flex items-center gap-2">

    <!-- PLAY BUTTON -->
    <button
      @click="togglePlay"
      class="p-3 rounded-full bg-black/50 hover:bg-black/70 transition flex items-center justify-center"
    >
      <PlayIcon
        v-if="isPaused"
        class="text-white/90 w-8 h-8"
      />
      <PauseIcon
        v-else
        class="text-white/90 w-8 h-8"
      />
    </button>

    <!-- VOLUME GROUP -->
    <div class="flex items-center gap-2 px-3 py-2 rounded-full bg-black/50">
      <button
        @click="toggleMute"
        class="hover:text-[#2D8CFF] transition"
      >
        <SpeakerXMarkIcon
          v-if="isMuted || volume === 0"
          class="text-white/90 w-7 h-7"
        />
        <SpeakerWaveIcon
          v-else
          class="text-white/90 w-7 h-7"
        />
      </button>

              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                v-model.number="volume"
                @input="onVolumeChange"
                class="w-20 cursor-pointer accent-[#2D8CFF] bg-transparent border-none
                      focus:outline-none focus:ring-0 focus:border-transparent appearance-none"
                      :style="volumeTrackStyle"
              />

    </div>

    <!-- TIME DISPLAY -->
    <div class="px-3 py-2 rounded-full bg-black/50 text-sm">
      {{ formattedTime }} / {{ formattedDuration }}
    </div>

  </div>

  <!-- RIGHT SIDE -->
  <div class="flex items-center gap-2">

    <!-- FULLSCREEN -->
    <!-- <button
      @click="toggleFullscreen"
      class="p-3 rounded-full bg-black/50 hover:bg-black/70 transition flex items-center justify-center"
    >
      <ArrowsPointingOutIcon class="text-white/90 w-7 h-7" />
    </button> -->

  </div>
</div>




      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from "vue";
import Hls from "hls.js";

import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  // ArrowsPointingOutIcon,
} from "@heroicons/vue/24/solid";

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  fileId: { type: String, required: true },
  playlistUrl: { type: String, default: "" },
  // 👇 NEW: tells the player to use the chat HLS route instead of media-drive route
  chatMode: { type: Boolean, default: false },
});

const videoEl = ref(null);
const progressBarEl = ref(null);

let hlsInstance = null;

const HLS_VOD_BUFFER_CONFIG = {
  maxBufferLength: 60,
  maxMaxBufferLength: 120,
  maxBufferSize: 90 * 1000 * 1000,
  backBufferLength: 45,
  manifestLoadingMaxRetry: 6,
  levelLoadingMaxRetry: 6,
  fragLoadingMaxRetry: 8,
  fragLoadingRetryDelay: 1000,
  fragLoadingMaxRetryTimeout: 10000,
};

const volume = ref(1);
const isMuted = ref(false);

const volumeTrackStyle = computed(() => ({
  '--volume-percent': `${volume.value * 100}%`,
}));


/* -------------------------------------------------
   HLS INITIALIZATION
-------------------------------------------------- */
function loadVideoSource(playlistUrl) {
  const video = videoEl.value;
  if (!video || !playlistUrl) return;

  // show spinner for a new load
  isLoading.value = true;

  // clean previous HLS instance
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }

  // Native HLS devices (iOS/Safari)
  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = playlistUrl;
    // <video @loadeddata="onVideoLoaded"> will hide spinner
    video.load();
    return;
  }

  // Other browsers (Hls.js)
  if (Hls.isSupported()) {
    const hls = new Hls({
      debug: false,
      ...HLS_VOD_BUFFER_CONFIG,
      lowLatencyMode: false,
      capLevelToPlayerSize: true,
      startFragPrefetch: true,

      xhrSetup(xhr) {
        // propagate cookies / JWT through proxy
        xhr.withCredentials = true;
      },
    });

    hlsInstance = hls;

    // Prefer lowest level first for fastest start
    hls.startLevel = 0;

    hls.loadSource(playlistUrl);
    hls.attachMedia(video);

    // As soon as any data is buffered, hide the spinner
    hls.on(Hls.Events.BUFFER_APPENDED, () => {
      if (isLoading.value) {
        isLoading.value = false;
      }
    });

    // Keep MANIFEST_PARSED simple; Hls will load first segment
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      // no video.load() here – it can cancel segment requests on some browsers
    });

    hls.on(Hls.Events.ERROR, (event, data) => {
      console.warn("[HLS ERROR]", data);

      if (!data?.fatal) {
        if (data?.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
          hls.startLoad();
        }
        return;
      }

      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError();
            break;
          default:
            hls.destroy();
            break;
        }
      }
    });
  } else {
    console.error("HLS unsupported in this browser.");
  }
}

/* -------------------------------------------------
   PREFLIGHT / PLAYLIST PREP (KEEPS JWT FLOW)
   NOTE: returns URL immediately, fetch is fire-and-forget
-------------------------------------------------- */
async function prepareHlsRequest(fileId, explicitUrl) {
  if (explicitUrl && String(explicitUrl).trim().length) {
    const normalized = String(explicitUrl).trim();
    fetch(normalized, {
      method: "GET",
      credentials: "include",
    }).catch((err) => {
      console.error("Error prefetching HLS playlist:", err);
    });
    return normalized;
  }

  if (!apiProxyUrl) {
    console.error("[PlatformaVideoPlayer] VUE_APP_API_PROXY_URL is missing");
    return "";
  }

  // 👇 KEY CHANGE:
  // - For chat videos: use /api/chat-videos/master/:fileId
  // - For media-drive videos: use /api/stream-hls/playlist/:fileId
  const basePath = props.chatMode
    ? "/api/chat-videos/master/"
    : "/api/stream-hls/playlist/";

  const url = `${apiProxyUrl}${basePath}${encodeURIComponent(fileId)}`;

  // Fire-and-forget preflight so cookies / JWT are honored
  fetch(url, {
    method: "GET",
    credentials: "include",
  }).catch((err) => {
    console.error("Error prefetching HLS playlist:", err);
  });

  // immediately return the URL (no network delay)
  return url;
}

/* -------------------------------------------------
   WATCHER — waits for BOTH fileId AND videoEl
-------------------------------------------------- */
watch(
  [() => props.fileId, () => props.playlistUrl, videoEl],
  async ([id, explicitUrl, video]) => {
    if (!id || !video) return;

    const playlistUrl = await prepareHlsRequest(id, explicitUrl);
    if (!playlistUrl) return;

    loadVideoSource(playlistUrl);
  },
  { immediate: true }
);

/* -------------------------------------------------
   ORIGINAL PLAYER LOGIC
-------------------------------------------------- */

const isPaused = ref(true);
const isLoading = ref(true);

const duration = ref(0);
const currentTime = ref(0);
const progress = ref(0);

const showControls = ref(true);
const showOverlay = ref(true);

const isHoveringBar = ref(false);
const hoverPercent = ref(0);
const hoverTime = ref(0);

const isDragging = ref(false);
const pointerInsideControls = ref(false);

let hideTimeout = null;
let mouseUpListener = null;

// When first frame loads (bound via @loadeddata on <video>)
function onVideoLoaded() {
  isLoading.value = false;

  if (videoEl.value) {
    videoEl.value.volume = volume.value;
  }
}

// Time formatting
const format = (n) => {
  if (!n || Number.isNaN(n)) return "0:00";
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

const formattedTime = computed(() => format(currentTime.value));
const formattedDuration = computed(() => format(duration.value));
const hoverFormattedTime = computed(() => format(hoverTime.value));

// Play/Pause toggle
function togglePlay() {
  const video = videoEl.value;
  if (!video) return;

  if (video.paused) {
    video.play();
    isPaused.value = false;
    showOverlay.value = false;
  } else {
    video.pause();
    isPaused.value = true;
    showOverlay.value = true;
  }

  showUi();
}

// Progress tracking
function updateProgress() {
  if (!videoEl.value || !duration.value) return;
  currentTime.value = videoEl.value.currentTime;
  progress.value = (currentTime.value / duration.value) * 100;
}

function setDuration() {
  if (!videoEl.value) return;
  duration.value = videoEl.value.duration || 0;
}

function seekToPercent(percent) {
  const video = videoEl.value;
  if (!video || !duration.value) return;

  const clamped = Math.min(1, Math.max(0, percent));
  video.currentTime = clamped * duration.value;
  currentTime.value = video.currentTime;
  progress.value = clamped * 100;
}

// Hover behavior
function updateHoverFromEvent(e) {
  if (!progressBarEl.value || !duration.value) return;

  const rect = progressBarEl.value.getBoundingClientRect();
  let percent = (e.clientX - rect.left) / rect.width;
  percent = Math.min(1, Math.max(0, percent));

  hoverPercent.value = percent * 100;
  hoverTime.value = percent * duration.value;
}

function onBarMouseMove(e) {
  if (!duration.value) return;
  isHoveringBar.value = true;
  updateHoverFromEvent(e);

  if (isDragging.value) {
    seekToPercent(hoverPercent.value / 100);
  }
}

function onBarMouseLeave() {
  isHoveringBar.value = false;

  if (!isPaused.value && !pointerInsideControls.value) {
    hideUiDelayed();
  }
}

function onBarMouseDown(e) {
  if (!duration.value) return;

  isDragging.value = true;
  updateHoverFromEvent(e);
  seekToPercent(hoverPercent.value / 100);
  showUi();

  mouseUpListener = () => {
    isDragging.value = false;
    window.removeEventListener("mouseup", mouseUpListener);
    mouseUpListener = null;

    if (!pointerInsideControls.value && !isPaused.value) {
      hideUiDelayed();
    }
  };

  window.addEventListener("mouseup", mouseUpListener);
}

// Volume
function onVolumeChange() {
  const video = videoEl.value;
  if (!video) return;

  video.muted = false;
  isMuted.value = false;
  video.volume = volume.value;
}

function toggleMute() {
  const video = videoEl.value;
  if (!video) return;

  if (isMuted.value || volume.value === 0) {
    if (volume.value === 0) volume.value = 0.5;
    video.muted = false;
    isMuted.value = false;
    video.volume = volume.value;
  } else {
    video.muted = true;
    isMuted.value = true;
  }
}

// Autohide UI
function showUi() {
  showControls.value = true;
  clearTimeout(hideTimeout);

  hideTimeout = setTimeout(() => {
    if (
      !isPaused.value &&
      !isDragging.value &&
      !isHoveringBar.value &&
      !pointerInsideControls.value
    ) {
      showControls.value = false;
    }
  }, 2000);
}

function hideUiDelayed() {
  clearTimeout(hideTimeout);

  hideTimeout = setTimeout(() => {
    if (
      !isPaused.value &&
      !isDragging.value &&
      !isHoveringBar.value &&
      !pointerInsideControls.value
    ) {
      showControls.value = false;
    }
  }, 1500);
}

function onControlsEnter() {
  pointerInsideControls.value = true;
  clearTimeout(hideTimeout);
  showControls.value = true;
}

function onControlsLeave() {
  pointerInsideControls.value = false;

  if (!isPaused.value && !isHoveringBar.value && !isDragging.value) {
    hideUiDelayed();
  }
}

// Cleanup
onUnmounted(() => {
  if (mouseUpListener) {
    window.removeEventListener("mouseup", mouseUpListener);
  }
  clearTimeout(hideTimeout);

  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }
});
</script>

<style scoped>
/* Fade animation for controls */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Fade animation for loading overlay */
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.3s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}

/* Range input basic reset (keeps Tailwind accent color) */
input[type='range'] {
  background: transparent;
}

/* ─────────────────────────────────────────────
   Custom <input type="range"> styling
   (used for volume in the chat video player)
────────────────────────────────────────────── */

/* Base range reset */
input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
}

/* WebKit track with dynamic fill using CSS variable */
input[type='range']::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    #2d8cff 0%,
    #2d8cff var(--volume-percent, 50%),
    rgba(255, 255, 255, 0.25) var(--volume-percent, 50%),
    rgba(255, 255, 255, 0.25) 100%
  );
}

/* WebKit thumb */
input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background: #2d8cff;
  margin-top: -4px; /* centers thumb on 4px track */
}

/* Firefox track */
input[type='range']::-moz-range-track {
  height: 4px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.25);
}

/* Firefox filled portion */
input[type='range']::-moz-range-progress {
  height: 4px;
  border-radius: 9999px;
  background: #2d8cff;
}

/* Firefox thumb */
input[type='range']::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background: #2d8cff;
  border: none;
}
</style>

